/**
 * Client Portal Hooks - TanStack Query
 *
 * Hooks pour les données du portail client
 * Pattern: Component -> Hook -> Service -> Backend API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { notifications } from '@/lib/notifications';
import { getErrorMessage } from '@/lib/errorHandler';
import {
  clientPortalDashboardService,
  clientPortalProjectsService,
  clientPortalSituationsService,
  clientPortalMessagesService,
  clientPortalDocumentsService,
  clientPortalTimelineService,
} from '../services/client-portal.service';
import type {
  ValidateSituationInput,
  SendMessageInput,
  ClientDocumentsFilter,
} from '../types/client-portal.types';

// ==================== QUERY KEYS ====================

export const clientPortalKeys = {
  all: ['client-portal'] as const,

  // Dashboard
  dashboard: () => [...clientPortalKeys.all, 'dashboard'] as const,

  // Projects
  projects: () => [...clientPortalKeys.all, 'projects'] as const,
  projectsList: () => [...clientPortalKeys.projects(), 'list'] as const,
  projectDetail: (id: string) => [...clientPortalKeys.projects(), id] as const,

  // Situations
  situations: () => [...clientPortalKeys.all, 'situations'] as const,
  situationsPending: () => [...clientPortalKeys.situations(), 'pending'] as const,
  situationsByProject: (projectId: string) =>
    [...clientPortalKeys.situations(), 'project', projectId] as const,
  situationDetail: (projectId: string, situationId: string) =>
    [...clientPortalKeys.situations(), projectId, situationId] as const,

  // Messages
  messages: () => [...clientPortalKeys.all, 'messages'] as const,
  conversations: () => [...clientPortalKeys.messages(), 'conversations'] as const,
  unreadCount: () => [...clientPortalKeys.messages(), 'unread-count'] as const,
  messagesByProject: (projectId: string) =>
    [...clientPortalKeys.messages(), 'project', projectId] as const,

  // Documents (Sprint 3.3 - US-3.3.3)
  documents: () => [...clientPortalKeys.all, 'documents'] as const,
  documentsRecent: () => [...clientPortalKeys.documents(), 'recent'] as const,
  documentsByProject: (projectId: string) =>
    [...clientPortalKeys.documents(), 'project', projectId] as const,
  documentStats: (projectId: string) =>
    [...clientPortalKeys.documents(), 'stats', projectId] as const,
  documentDetail: (documentId: string) =>
    [...clientPortalKeys.documents(), documentId] as const,

  // Timeline (Sprint 3.3 - US-3.3.1)
  timeline: () => [...clientPortalKeys.all, 'timeline'] as const,
  projectTimeline: (projectId: string) =>
    [...clientPortalKeys.timeline(), 'project', projectId] as const,
  projectPhases: (projectId: string) =>
    [...clientPortalKeys.timeline(), 'phases', projectId] as const,
  projectPhotos: (projectId: string) =>
    [...clientPortalKeys.timeline(), 'photos', projectId] as const,
  photoDetail: (photoId: string) =>
    [...clientPortalKeys.timeline(), 'photo', photoId] as const,
};

// ==================== DASHBOARD ====================

export function useClientPortalDashboard() {
  return useQuery({
    queryKey: clientPortalKeys.dashboard(),
    queryFn: () => clientPortalDashboardService.getStats(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ==================== PROJECTS ====================

export function useClientProjects() {
  return useQuery({
    queryKey: clientPortalKeys.projectsList(),
    queryFn: () => clientPortalProjectsService.getAll(),
    staleTime: 2 * 60 * 1000,
  });
}

export function useClientProject(id: string) {
  return useQuery({
    queryKey: clientPortalKeys.projectDetail(id),
    queryFn: () => clientPortalProjectsService.getById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

// ==================== SITUATIONS ====================

export function usePendingSituations() {
  return useQuery({
    queryKey: clientPortalKeys.situationsPending(),
    queryFn: () => clientPortalSituationsService.getPending(),
    staleTime: 1 * 60 * 1000, // 1 minute - données plus critiques
  });
}

export function useProjectSituations(projectId: string) {
  return useQuery({
    queryKey: clientPortalKeys.situationsByProject(projectId),
    queryFn: () => clientPortalSituationsService.getByProject(projectId),
    enabled: !!projectId,
    staleTime: 1 * 60 * 1000,
  });
}

export function useSituation(projectId: string, situationId: string) {
  return useQuery({
    queryKey: clientPortalKeys.situationDetail(projectId, situationId),
    queryFn: () => clientPortalSituationsService.getById(projectId, situationId),
    enabled: !!projectId && !!situationId,
    staleTime: 1 * 60 * 1000,
  });
}

export function useValidateSituation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      situationId,
      input,
    }: {
      projectId: string;
      situationId: string;
      input: ValidateSituationInput;
    }) => clientPortalSituationsService.validate(projectId, situationId, input),

    onSuccess: (_, { projectId, situationId }) => {
      // Invalider toutes les queries liées
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationDetail(projectId, situationId) });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationsByProject(projectId) });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationsPending() });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.projectDetail(projectId) });

      toast.success('Situation validée avec succès');
    },

    onError: (error: Error) => {
      notifications.error(getErrorMessage(error));
    },
  });
}

export function useRejectSituation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      situationId,
      reason,
    }: {
      projectId: string;
      situationId: string;
      reason: string;
    }) => clientPortalSituationsService.reject(projectId, situationId, reason),

    onSuccess: (_, { projectId, situationId }) => {
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationDetail(projectId, situationId) });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationsByProject(projectId) });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationsPending() });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.dashboard() });

      toast.success('Situation rejetée');
    },

    onError: (error: Error) => {
      notifications.error(getErrorMessage(error));
    },
  });
}

export function useDisputeSituation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      situationId,
      reason,
    }: {
      projectId: string;
      situationId: string;
      reason: string;
    }) => clientPortalSituationsService.dispute(projectId, situationId, reason),

    onSuccess: (_, { projectId, situationId }) => {
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationDetail(projectId, situationId) });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationsByProject(projectId) });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.situationsPending() });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.dashboard() });

      toast.success('Contestation enregistrée');
    },

    onError: (error: Error) => {
      notifications.error(getErrorMessage(error));
    },
  });
}

// ==================== MESSAGES ====================

export function useConversations() {
  return useQuery({
    queryKey: clientPortalKeys.conversations(),
    queryFn: () => clientPortalMessagesService.getConversations(),
    staleTime: 30 * 1000, // 30 secondes
  });
}

export function useUnreadMessagesCount() {
  return useQuery({
    queryKey: clientPortalKeys.unreadCount(),
    queryFn: () => clientPortalMessagesService.getUnreadCount(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Polling toutes les minutes
  });
}

export function useProjectMessages(projectId: string) {
  return useQuery({
    queryKey: clientPortalKeys.messagesByProject(projectId),
    queryFn: () => clientPortalMessagesService.getByProject(projectId),
    enabled: !!projectId,
    staleTime: 30 * 1000,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: string;
      input: SendMessageInput;
    }) => clientPortalMessagesService.send(projectId, input),

    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.messagesByProject(projectId) });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: clientPortalKeys.unreadCount() });

      toast.success('Message envoyé');
    },

    onError: (error: Error) => {
      notifications.error(getErrorMessage(error));
    },
  });
}

// ==================== DOCUMENTS (Sprint 3.3 - US-3.3.3) ====================

/**
 * Hook pour récupérer les documents d'un projet
 */
export function useProjectDocuments(projectId: string, filter?: ClientDocumentsFilter) {
  return useQuery({
    queryKey: [...clientPortalKeys.documentsByProject(projectId), filter],
    queryFn: () => clientPortalDocumentsService.getByProject(projectId, filter),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook pour récupérer les statistiques des documents d'un projet
 */
export function useProjectDocumentStats(projectId: string) {
  return useQuery({
    queryKey: clientPortalKeys.documentStats(projectId),
    queryFn: () => clientPortalDocumentsService.getStats(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook pour récupérer un document par ID
 */
export function useDocument(documentId: string) {
  return useQuery({
    queryKey: clientPortalKeys.documentDetail(documentId),
    queryFn: () => clientPortalDocumentsService.getById(documentId),
    enabled: !!documentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook pour récupérer les documents récents sur tous les projets
 */
export function useRecentDocuments(limit: number = 10) {
  return useQuery({
    queryKey: [...clientPortalKeys.documentsRecent(), limit],
    queryFn: () => clientPortalDocumentsService.getRecent(limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ==================== TIMELINE (Sprint 3.3 - US-3.3.1) ====================

/**
 * Hook pour récupérer la timeline complète d'un projet (phases + photos)
 */
export function useProjectTimeline(projectId: string) {
  return useQuery({
    queryKey: clientPortalKeys.projectTimeline(projectId),
    queryFn: () => clientPortalTimelineService.getTimeline(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook pour récupérer les phases d'un projet
 */
export function useProjectPhases(projectId: string) {
  return useQuery({
    queryKey: clientPortalKeys.projectPhases(projectId),
    queryFn: () => clientPortalTimelineService.getPhases(projectId),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook pour récupérer les photos d'un projet
 */
export function useProjectPhotos(projectId: string, limit?: number) {
  return useQuery({
    queryKey: [...clientPortalKeys.projectPhotos(projectId), limit],
    queryFn: () => clientPortalTimelineService.getPhotos(projectId, limit),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook pour récupérer une photo par ID
 */
export function usePhoto(photoId: string) {
  return useQuery({
    queryKey: clientPortalKeys.photoDetail(photoId),
    queryFn: () => clientPortalTimelineService.getPhoto(photoId),
    enabled: !!photoId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
